import React, { useState, useEffect, useContext } from "react";
import { TreeNodeModel, Client } from "../tree/types";
import { Branch, RegistrationType } from '../../models/interfaces';
import { ApplicationsContext } from "../../context/applications/ApplicationContext";
import Message from "../ui/Message";
import Loading from "../ui/Loading";
import { AuthContext } from "../../context/auth";

interface Props {
  node: TreeNodeModel;
  onSave: (node: TreeNodeModel, client: Client) => void;
  onChange: (node: TreeNodeModel) => void; // ⭐ NUEVO
  readOnly?: boolean;
}

export const ClientForm: React.FC<Props> = ({ node, onSave, readOnly, onChange }) => {
  const { loadRegistrationTypes, loadBranches } = useContext(ApplicationsContext);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useContext(AuthContext);

  const [registrationTypes, setRegistrationTypes] = useState<RegistrationType[]>([]);
  const [branchs, setBranchs] = useState<Branch[]>([]);
  const [errorMessage, setErrorMessage] = useState("");

  const [form, setForm] = useState<Client>({
    id: "",
    clientType: "MERCHANT_RESIDENT",
    registrationType: "",
    companyName: "",
    tradeName: "",
    shortName: "",
    legalRep: { firstName: "", lastName: "" },
    merchantNumber: "",
    registrationNumber: "",
    taxpayerId: "",
    branch: "",
    reason: "",
    status: "DRAFT",
    createdAt: new Date()
  });

  /* =====================
     LOAD DATA
     ===================== */
  useEffect(() => {
    setTimeout(() => {
      if (node.type === "CLIENT" && node.entityData) {
        setForm(structuredClone(node.entityData as Client));
      }
      setIsLoading(false);
    }, 600);
  }, [node.id]);

  useEffect(() => {
    loadRegistrationTypes().then(regs => setRegistrationTypes(regs));
    loadBranches().then(brns => setBranchs(brns));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {

    let { name, value } = e.target;


    if (name == "registrationNumber" || name === "merchantNumber") {
      value = value.trim();
    }

    let updatedForm: Client;

    if (name === "firstName" || name === "lastName") {

      updatedForm = {
        ...form,
        legalRep: {
          ...form.legalRep,
          [name]: value
        }
      };

    } else {

      updatedForm = {
        ...form,
        [name]: value
      };

    }

    setForm(updatedForm);

    // ⭐ Actualiza árbol en memoria (sin BD)
    onChange({
      ...node,
      name: updatedForm.companyName,
      saved: false,
      entityData: updatedForm
    });

  };

  const removeError = () => setErrorMessage("");

  /* =====================
     SAVE
     ===================== */
  const handleSave = () => {
    const requiredFields = [
      "branch",
      "reason",
      "registrationType",
      "merchantNumber",
      "companyName",
      "tradeName",
      "shortName",
      "registrationNumber",
      "legalRep.firstName",
      "legalRep.lastName"
    ];

    for (const field of requiredFields) {
      const value = field.includes("legalRep")
        ? form.legalRep[field.split(".")[1] as keyof typeof form.legalRep]
        : (form as any)[field];

      if (!value || value === "-1") {
        setErrorMessage("Por favor complete los campos requeridos.");
        return;
      }
    }

    onSave(
      {
        ...node,
        name: form.companyName,
        saved: true,
        entityData: form
      },
      form
    );
  };

  /* =====================
     RENDER
     ===================== */
  return (
    <>
      {isLoading ? (
        <Loading message="" />
      ) : (
        <div className="container-fluid">
          <h5 className="mb-3">Cliente</h5>

          <div className="row">
            {/* Branch */}
            <div className="col-md-6 mb-3">
              <label className="form-label">Sucursal *</label>
              <select
                name="branch"
                className="form-select"
                value={form.branch}
                onChange={handleChange}
                disabled={readOnly}
              >
                <option value={-1}>Seleccione una opción</option>
                {branchs.map(branch => (
                  <option key={branch.id} value={branch.code}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Reason */}
            <div className="col-md-6 mb-3">
              <label className="form-label">Motivo / Comentario *</label>
              <input
                name="reason"
                className="form-control"
                value={form.reason}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>

            {/* Registration Type */}
            {<div className="col-md-6 mb-3">
              <label className="form-label">Tipo de Persona *</label>
              <select
                name="registrationType"
                className="form-select"
                value={form.registrationType}
                onChange={handleChange}
                disabled={readOnly}
              >
                <option value={-1}>Seleccione una opción</option>
                {registrationTypes.map(reg => (
                  <option key={reg.id} value={reg.code}>
                    {reg.description}
                  </option>
                ))}
              </select>
            </div>}

            {/* Registration Number */}
            {<div className="col-md-6 mb-3">
              <label className="form-label">Número Id Fiscal *</label>
              <input
                name="registrationNumber"
                className="form-control"
                value={form.registrationNumber}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>}

            {/* Merchant Number */}
            <div className="col-md-6 mb-3">
              <label className="form-label">Número de Cliente - CIF *</label>
              <input
                name="merchantNumber"
                className="form-control"
                value={form.merchantNumber}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>

            {/* Company Name */}
            <div className="col-md-6 mb-3">
              <label className="form-label">Nombre comercial *</label>
              <input
                name="companyName"
                className="form-control"
                value={form.companyName}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>

            {/* Trade Name */}
            <div className="col-md-6 mb-3">
              <label className="form-label">{user.banks[0].code == '0051' ? "Nombre corto (Fantasía)" : "Nombre afiliado"} *</label>
              <input
                name="tradeName"
                className="form-control"
                value={form.tradeName}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>

            {/* Short Name */}
            <div className="col-md-6 mb-3">
              <label className="form-label">Nombre Corto *</label>
              <input
                name="shortName"
                className="form-control"
                value={form.shortName}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>



            {/* Taxpayer ID (always disabled) */}
            <div className="col-md-6 mb-3 visually-hidden">
              <label className="form-label">Taxpayer ID</label>
              <input
                className="form-control"
                value={form.registrationNumber}
                disabled
              />
            </div>
          </div>

          <hr />

          <h6 className="mb-3">Representante Legal</h6>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Nombre *</label>
              <input
                name="firstName"
                className="form-control"
                value={form.legalRep.firstName}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Apellido *</label>
              <input
                name="lastName"
                className="form-control"
                value={form.legalRep.lastName}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>
          </div>

          {!readOnly && (
            <button className="btn btn-primary mt-3" onClick={handleSave}>
              Guardar Cliente
            </button>
          )}

          {errorMessage && (
            <Message
              type="error"
              message={errorMessage}
              closeDialog={removeError}
              handleOk={removeError}
              backgroundColor="rgba(0,0,0,0.4)"
            />
          )}
        </div>
      )}
    </>
  );
};
